import type { Fps, Quality } from "@sp/shared/enums";
import { Button } from "../../components/ui/Button";
import { cx } from "../../components/ui/cx";
import type { Video } from "../../lib/api/types";
import { formatDuration } from "../../lib/format";
import { profileLabel, readyProfiles, videoFit } from "../../lib/stream-form";

interface Props {
  videos: Video[];
  value: string[];
  onChange: (ids: string[]) => void;
  /** The stream's quality and frame rate: only videos with a ready version for them can be added. */
  quality: Quality;
  fps: Fps;
  error?: string | null;
}

/** The ready versions of a video as small tags, the one this stream would use highlighted. */
function Profiles({ video, wanted }: { video: Video; wanted: string }) {
  const ready = readyProfiles(video);
  if (!ready.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {ready.map((p) => (
        <span key={p} className={cx("rounded border px-1 font-mono text-[11px] leading-4",
          p === wanted ? "border-ink bg-ink text-white" : "border-line text-muted")}>{p}</span>
      ))}
    </span>
  );
}

/**
 * Ordered playlist on the left, the library's ready videos to add on the right. Buttons only: no drag library.
 * Videos without a ready version at the chosen quality and fps are listed last, greyed out, with the reason.
 */
export function PlaylistPicker({ videos, value, onChange, quality, fps, error }: Props) {
  const byId = new Map(videos.map((v) => [v.id, v]));
  const wanted = profileLabel(quality, fps);
  const orientation = value.length ? byId.get(value[0]!)?.orientation : null;
  const library = videos
    .filter((v) => v.status === "ready")
    .map((v) => {
      const fit = videoFit(v, quality, fps);
      const mismatch = !!orientation && !!v.orientation && v.orientation !== orientation;
      const why = mismatch ? `the playlist is ${orientation}` : fit.ok ? null : fit.reason;
      return { v, why };
    })
    .sort((a, b) => Number(a.why !== null) - Number(b.why !== null));
  const move = (i: number, d: -1 | 1) => {
    const next = [...value];
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    onChange(next);
  };

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1.5">playlist <span className="font-mono text-xs text-muted">· streams at {wanted} fps</span></legend>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col overflow-hidden rounded-xl border-2 border-ink bg-card shadow-hard-sm">
          <span className="border-b-2 border-ink px-3 py-2 font-mono text-xs uppercase tracking-widest text-muted">in this order</span>
          <ol className="flex flex-col">
            {value.map((id, i) => {
              const video = byId.get(id);
              const fit = video ? videoFit(video, quality, fps) : null;
              return (
                <li key={`${id}-${i}`} className={cx("flex items-center gap-2 border-b border-line px-3 py-1.5 last:border-b-0", fit && !fit.ok && "bg-accent-tint")}>
                  <span className="font-mono text-muted">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{video?.name ?? "(video removed)"}</span>
                    {fit && !fit.ok && <span className="text-xs font-bold text-link-hover">can't stream at {wanted} fps: {fit.reason}</span>}
                  </span>
                  <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label="move up">↑</Button>
                  <Button size="sm" variant="ghost" disabled={i === value.length - 1} onClick={() => move(i, 1)} aria-label="move down">↓</Button>
                  <Button size="sm" variant="ghost" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="remove">✕</Button>
                </li>
              );
            })}
            {value.length === 0 && <li className="px-3 py-3 text-muted">// add videos from the library</li>}
          </ol>
        </div>
        <div className="flex flex-col overflow-hidden rounded-xl border-2 border-dashed border-ink bg-card">
          <span className="border-b-2 border-dashed border-ink px-3 py-2 font-mono text-xs uppercase tracking-widest text-muted">library (ready videos)</span>
          <ul className="flex max-h-72 flex-col overflow-y-auto">
            {library.map(({ v, why }) => (
              <li key={v.id} className="flex items-center gap-2 border-b border-line px-3 py-1.5 last:border-b-0">
                <span className={cx("flex min-w-0 flex-1 flex-col gap-0.5", why && "opacity-60")}>
                  <span className="truncate" title={v.name}>{v.name}</span>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                    {formatDuration(v.durationMs)} · {v.orientation ?? "?"}
                    <Profiles video={v} wanted={wanted} />
                  </span>
                  {why && <span className="text-xs">can't add: {why}</span>}
                </span>
                <Button size="sm" disabled={why !== null} title={why ?? undefined} onClick={() => onChange([...value, v.id])} aria-label={`add ${v.name}`}>+</Button>
              </li>
            ))}
            {library.length === 0 && <li className="px-3 py-3 text-muted">// no processed videos yet. upload one under videos.</li>}
          </ul>
        </div>
      </div>
      {error && <span role="alert" className="text-xs font-bold text-link-hover">{error}</span>}
    </fieldset>
  );
}
